// Prospection terrain — accès aux données de la carte.
//
// Le référentiel des communes et les tournées sont lus depuis notre base : le
// navigateur n'interroge aucun service tiers pour les données. Seules les
// TUILES du fond de carte viennent de la Géoplateforme IGN, établissement
// public français (voir CarteProspection).

import { analyserProspection, cleLieu } from '@imp/shared/prospection';
import { passagesRecents } from '@imp/shared/zone-prospection';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

export interface Commune {
  code: string;
  nom: string;
  nomCle: string;
  departement: string;
  latitude: number;
  longitude: number;
  population: number | null;
}

export interface Passage {
  id: string;
  date_passage: string;
  commune_code: string | null;
  commune_brute: string | null;
  auteur: string;
  flyers: number;
  argus: number;
  apporteurs: number;
  rdv: number;
  message: string | null;
  fiabilite: string;
  raison: string | null;
  /** « whatsapp » (import) ou « saisie » (ajouté à la main dans le CRM). */
  source: string;
}

/** Une commune, augmentée de ce qu'on y a fait. */
export interface CommuneProspectee extends Commune {
  passages: number;
  /** Passages des 12 derniers mois : c'est eux qui colorent la zone. */
  passages12Mois: number;
  flyers: number;
  argus: number;
  /** Date du dernier passage (AAAA-MM-JJ), null si jamais démarchée. */
  dernierPassage: string | null;
  /** Jours écoulés depuis le dernier passage, null si jamais démarchée. */
  anciennete: number | null;
}

export async function listCommunes(): Promise<Commune[]> {
  const lignes = await lireTout<{
    code: string;
    nom: string;
    nom_cle: string;
    departement: string;
    latitude: number | string;
    longitude: number | string;
    population: number | null;
  }>((de, a) =>
    supabase
      .from('prospection_communes')
      .select('code,nom,nom_cle,departement,latitude,longitude,population')
      .eq('organization_id', ORG_ID)
      .is('deleted_at', null)
      .order('code')
      .range(de, a),
  );
  return lignes.map((c) => ({
    code: c.code,
    nom: c.nom,
    nomCle: c.nom_cle,
    departement: c.departement,
    latitude: Number(c.latitude),
    longitude: Number(c.longitude),
    population: c.population,
  }));
}

export async function listPassages(): Promise<Passage[]> {
  return lireTout<Passage>((de, a) =>
    supabase
      .from('prospection_passages')
      .select(
        'id,date_passage,commune_code,commune_brute,auteur,flyers,argus,apporteurs,rdv,message,fiabilite,raison,source',
      )
      .eq('organization_id', ORG_ID)
      .is('deleted_at', null)
      .order('date_passage', { ascending: false })
      .range(de, a),
  );
}

/**
 * Croise communes et passages.
 *
 * `aujourdhuiMs` est passé en paramètre plutôt que lu de l'horloge : la
 * fonction reste pure et testable.
 */
export function croiser(
  communes: Commune[],
  passages: Passage[],
  aujourdhuiMs: number,
): CommuneProspectee[] {
  const aujourdhui = new Date(aujourdhuiMs).toISOString().slice(0, 10);
  const par = new Map<string, Passage[]>();
  for (const p of passages) {
    if (!p.commune_code) continue;
    const l = par.get(p.commune_code);
    if (l) l.push(p);
    else par.set(p.commune_code, [p]);
  }
  return communes.map((c) => {
    const l = par.get(c.code) ?? [];
    const dernier =
      l
        .map((p) => p.date_passage)
        .sort()
        .at(-1) ?? null;
    return {
      ...c,
      passages: l.length,
      passages12Mois: passagesRecents(
        l.map((p) => p.date_passage),
        aujourdhui,
      ),
      flyers: l.reduce((t, p) => t + p.flyers, 0),
      argus: l.reduce((t, p) => t + p.argus, 0),
      dernierPassage: dernier,
      anciennete: dernier
        ? Math.floor((aujourdhuiMs - Date.parse(`${dernier}T12:00:00Z`)) / 86_400_000)
        : null,
    };
  });
}

/**
 * Importe un export WhatsApp depuis le navigateur.
 *
 * Même analyseur que le script serveur : les deux chemins ne peuvent pas
 * diverger sur la lecture des rapports.
 */
export async function importerExportWhatsApp(
  fichier: File,
): Promise<{ repris: number; situes: number; aSituer: number }> {
  const communes = await listCommunes();
  if (communes.length === 0) {
    throw new Error(
      'Le référentiel des communes est vide. Il doit être chargé une fois depuis le serveur (RUNBOOK §27).',
    );
  }
  const parCle = new Map(communes.map((c) => [c.nomCle, c.code]));
  const { passages } = analyserProspection(await fichier.text(), [...parCle.keys()]);
  if (passages.length === 0) throw new Error('aucun rapport de tournée reconnu dans ce fichier');

  const lignes = passages.map((p) => ({
    organization_id: ORG_ID,
    date_passage: p.date,
    commune_code: p.communeBrute ? (parCle.get(p.communeBrute) ?? null) : null,
    commune_brute: p.communeBrute,
    auteur: p.auteur,
    flyers: p.flyers,
    argus: p.argus,
    apporteurs: p.apporteurs,
    rdv: p.rdv,
    message: p.message,
    fiabilite: p.fiabilite,
    raison: p.raison,
    source: 'whatsapp',
    empreinte: `${p.date}|${p.auteur}|${p.communeBrute ?? '-'}|${String(p.flyers)}|${p.message.slice(0, 120)}`,
  }));

  for (let i = 0; i < lignes.length; i += 500) {
    const { error } = await supabase
      .from('prospection_passages')
      .upsert(lignes.slice(i, i + 500), { onConflict: 'organization_id,empreinte' });
    if (error) throw new Error(error.message);
  }
  const situes = lignes.filter((l) => l.commune_code).length;
  return { repris: lignes.length, situes, aSituer: lignes.length - situes };
}

export interface NouveauPassage {
  date: string;
  commune: Commune;
  flyers: number;
  auteur: string;
}

/** Ajoute à la main un passage (« 300 flyers à Mane le 26/10 »). */
export async function ajouterPassage(p: NouveauPassage): Promise<void> {
  const { error } = await supabase.from('prospection_passages').insert({
    organization_id: ORG_ID,
    date_passage: p.date,
    commune_code: p.commune.code,
    commune_brute: p.commune.nom,
    auteur: p.auteur,
    flyers: p.flyers,
    source: 'saisie',
  });
  if (error) throw new Error(error.message);
}

/** Retire un passage saisi par erreur (il reste en base, marqué supprimé). */
export async function supprimerPassage(id: string): Promise<void> {
  const { error } = await supabase
    .from('prospection_passages')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('source', 'saisie');
  if (error) throw new Error(error.message);
}

/**
 * Retrouve la commune tapée : « Mane (04) » tel que proposé par la liste, ou
 * simplement « mane » quand un seul nom correspond.
 */
export function trouverCommune(communes: readonly Commune[], saisie: string): Commune | null {
  const t = saisie.trim();
  const exacte = communes.find((c) => libelleCommune(c) === t);
  if (exacte) return exacte;
  const cle = cleLieu(t.replace(/\s*\(\d{2,3}\)\s*$/, ''));
  const memeNom = communes.filter((c) => c.nomCle === cle);
  return memeNom.length === 1 ? (memeNom[0] ?? null) : null;
}

/** Le libellé proposé dans la liste des communes. */
export function libelleCommune(c: Commune): string {
  return `${c.nom} (${c.departement})`;
}

/** Rattache à la main un passage resté sans commune. */
export async function situerPassage(id: string, communeCode: string): Promise<void> {
  const { error } = await supabase
    .from('prospection_passages')
    .update({ commune_code: communeCode, fiabilite: 'sure', raison: null })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export { cleLieu };
